import React, { useState, useEffect } from 'react';
import { FirmAdminLayout } from './FirmAdminLayout';
import { ReportsManagement } from './ReportsManagement';
import { AdvancedAnalytics } from './AdvancedAnalytics';
import { DataExportIntegration } from './DataExportIntegration';
import { ReportLibrary } from './ReportLibrary';
import { ReportViewer } from './ReportViewer';
import { ReportSelector } from './ReportSelector';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
  Clock,
  FileText,
  Download,
  Calendar,
  RefreshCw,
  Mail,
  Eye,
  Trash2,
  Settings,
  Zap
} from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface AnalyticsData {
  overview: {
    totalRevenue: number;
    totalClients: number;
    activeClients: number;
    totalTasks: number;
    completedTasks: number;
    totalHours: number;
    billableHours: number;
    utilizationRate: number;
  };
  trends: {
    revenueGrowth: number;
    clientGrowth: number;
    taskCompletionRate: number;
    hoursGrowth: number;
  };
  charts: {
    revenueByMonth: Array<{ month: string; revenue: number }>;
    clientsByType: Array<{ type: string; count: number }>;
    tasksByStatus: Array<{ status: string; count: number }>;
    hoursDistribution: Array<{ category: string; hours: number }>;
  };
}

interface Report {
  id: string;
  name: string;
  type: string;
  description: string;
  status: 'ready' | 'processing' | 'failed';
  format: 'json' | 'pdf' | 'csv' | 'excel';
  generatedAt: string;
  size?: string;
}

export const ReportsAnalytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'analytics' | 'viewer' | 'advanced' | 'management' | 'export'>('analytics');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [viewerMode, setViewerMode] = useState<'library' | 'report'>('library');
  const [selectedReportType, setSelectedReportType] = useState<string | null>(null);



  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [refreshing, setRefreshing] = useState(false);

  // Error boundary fallback (already declared above)

  // Helper function to safely format numbers
  const safeToFixed = (value: any, decimals: number = 1): string => {
    const num = Number(value);
    return isNaN(num) || !isFinite(num) ? '0' : num.toFixed(decimals);
  };

  // Helper function to safely format currency
  const safeToLocaleString = (value: any): string => {
    const num = Number(value);
    return isNaN(num) || !isFinite(num) ? '0' : num.toLocaleString();
  };

  // Function declarations (moved before useEffect to avoid hoisting issues)
  const fetchAnalytics = async () => {
    try {
      setRefreshing(true);
      console.log('🔄 Fetching real analytics data from server...');

      // Fetch real analytics data from the server
      const response = await api.get('/firm-admin/analytics/dashboard', {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          period: selectedPeriod
        }
      });

      console.log('📊 Analytics API response:', response.data);

      if (response.data.success && response.data.data) {
        // Transform the server response to match our interface
        const serverData = response.data.data;

        // Helper function to safely convert to number
        const safeNumber = (value: any): number => {
          const num = Number(value);
          return isNaN(num) || !isFinite(num) ? 0 : num;
        };

        setAnalytics({
          overview: {
            totalRevenue: safeNumber(serverData.overview?.totalRevenue),
            totalClients: safeNumber(serverData.overview?.totalClients),
            activeClients: safeNumber(serverData.overview?.activeClients),
            totalTasks: safeNumber(serverData.overview?.totalTasks),
            completedTasks: safeNumber(serverData.overview?.completedTasks),
            totalHours: safeNumber(serverData.overview?.totalHours),
            billableHours: safeNumber(serverData.overview?.billableHours),
            utilizationRate: safeNumber(serverData.overview?.utilizationRate)
          },
          trends: {
            revenueGrowth: safeNumber(serverData.trends?.revenueGrowth),
            clientGrowth: safeNumber(serverData.trends?.clientGrowth),
            taskCompletionRate: safeNumber(serverData.trends?.taskCompletionRate),
            hoursGrowth: safeNumber(serverData.trends?.hoursGrowth)
          },
          charts: {
            revenueByMonth: Array.isArray(serverData.charts?.revenueByMonth) ? serverData.charts.revenueByMonth : [],
            clientsByType: Array.isArray(serverData.charts?.clientsByType) ? serverData.charts.clientsByType : [],
            tasksByStatus: Array.isArray(serverData.charts?.tasksByStatus) ? serverData.charts.tasksByStatus : [],
            hoursDistribution: Array.isArray(serverData.charts?.hoursDistribution) ? serverData.charts.hoursDistribution : []
          }
        });
        console.log('📊 Real analytics data loaded successfully');
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error: any) {
      console.error('❌ Error loading analytics:', error);

      // Set fallback data to prevent UI crashes
      setAnalytics({
        overview: {
          totalRevenue: 0,
          totalClients: 0,
          activeClients: 0,
          totalTasks: 0,
          completedTasks: 0,
          totalHours: 0,
          billableHours: 0,
          utilizationRate: 0
        },
        trends: {
          revenueGrowth: 0,
          clientGrowth: 0,
          taskCompletionRate: 0,
          hoursGrowth: 0
        },
        charts: {
          revenueByMonth: [],
          clientsByType: [],
          tasksByStatus: [],
          hoursDistribution: []
        }
      });

      // Show user-friendly error message
      toast.error('Failed to load analytics data. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchReports = async () => {
    try {
      console.log('🔄 Loading mock reports data for demo...');

      // Always use mock data to avoid API issues
      setReports([
        {
          id: '1',
          name: 'Monthly Financial Report',
          type: 'financial',
          description: 'Comprehensive monthly financial analysis and reporting',
          status: 'ready',
          generatedAt: '2024-01-15T10:30:00Z',
          size: '2.4 MB',
          format: 'pdf'
        },
        {
          id: '2',
          name: 'Client Activity Summary',
          type: 'client-activity',
          description: 'Summary of client interactions and activities',
          status: 'ready',
          generatedAt: '2024-01-10T14:20:00Z',
          size: '1.8 MB',
          format: 'excel'
        },
        {
          id: '3',
          name: 'Task Performance Analysis',
          type: 'task-performance',
          description: 'Analysis of task completion rates and performance metrics',
          status: 'processing',
          generatedAt: '2024-01-12T09:15:00Z',
          format: 'pdf'
        }
      ]);
      console.log('📋 Mock reports data loaded successfully');
    } catch (error: any) {
      console.error('❌ Error loading reports:', error);
    }
  };

  // Debug logging
  console.log('🔧 ReportsAnalytics component rendering...', {
    activeTab,
    loading,
    hasError,
    analytics: !!analytics,
    reports: reports.length,
    timestamp: new Date().toISOString()
  });

  // Debug: Log critical state changes only
  React.useEffect(() => {
    if (hasError) {
      console.log('❌ Error state detected');
    }
  }, [hasError]);



  if (hasError) {
    console.log('❌ Showing error state:', { hasError });
    return (
      <FirmAdminLayout>
        <div className="p-6">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="text-red-500 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Reports & Analytics Unavailable</h3>
              <p className="text-gray-600 mb-4">There was an error loading the reports section. Please try refreshing the page.</p>
              <button
                onClick={() => {
                  setHasError(false);
                  setLoading(true);
                  window.location.reload();
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      </FirmAdminLayout>
    );
  }

  // Simple loading state
  if (loading) {
    return (
      <FirmAdminLayout>
        <div className="p-6">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Reports & Analytics</h3>
              <p className="text-gray-600">Please wait while we load your data...</p>
            </div>
          </div>
        </div>
      </FirmAdminLayout>
    );
  }

  const tabs = [
    {
      id: 'analytics' as const,
      label: 'Analytics Dashboard',
      icon: BarChart3,
      description: 'View performance metrics and trends'
    },
    {
      id: 'viewer' as const,
      label: 'Report Viewer',
      icon: Eye,
      description: 'View and analyze reports on screen'
    },
    {
      id: 'advanced' as const,
      label: 'Advanced Analytics',
      icon: Zap,
      description: 'Trend analysis and comparative reports'
    },
    {
      id: 'management' as const,
      label: 'Report Management',
      icon: Settings,
      description: 'Generate, schedule, and manage reports'
    },
    {
      id: 'export' as const,
      label: 'Data Export & Integration',
      icon: Download,
      description: 'Export data and manage integrations'
    }
  ];



  // Initial data loading effect - only runs once on mount
  useEffect(() => {
    // Fetch real data from server on component mount
    fetchAnalytics();

    setReports([
      {
        id: '1',
        name: 'Monthly Financial Report',
        type: 'financial',
        description: 'Comprehensive monthly financial analysis and reporting',
        status: 'ready',
        generatedAt: '2024-01-15T10:30:00Z',
        size: '2.4 MB',
        format: 'pdf'
      },
      {
        id: '2',
        name: 'Client Activity Summary',
        type: 'client-activity',
        description: 'Summary of client interactions and activities',
        status: 'ready',
        generatedAt: '2024-01-10T14:20:00Z',
        size: '1.8 MB',
        format: 'excel'
      },
      {
        id: '3',
        name: 'Task Performance Analysis',
        type: 'task-performance',
        description: 'Analysis of task completion rates and performance metrics',
        status: 'processing',
        generatedAt: '2024-01-12T09:15:00Z',
        format: 'pdf'
      }
    ]);

    // Ensure loading is false
    setLoading(false);

  }, []); // Empty dependency array - only run once on mount

  // Separate effect for data refresh when filters change
  useEffect(() => {
    // Skip if this is the initial render (loading is still true)
    if (loading) {
      console.log('⏭️ Skipping filter refresh during initial load');
      return;
    }

    console.log('🔄 Filter change detected, refreshing analytics...', { dateRange, selectedPeriod });

    const refreshData = async () => {
      try {
        setRefreshing(true);
        await fetchAnalytics();
        console.log('✅ Analytics refreshed for new filters');
      } catch (error) {
        console.error('❌ Error refreshing analytics:', error);
        toast.error('Failed to refresh analytics data');
      } finally {
        setRefreshing(false);
      }
    };

    refreshData();
  }, [dateRange, selectedPeriod]); // Only refresh analytics when filters change

  const handleDateRangeChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handlePeriodChange = (period: string) => {
    console.log('📅 Period change requested:', period);

    // Auto-adjust date range based on period
    const endDate = new Date();
    let startDate = new Date();

    switch (period) {
      case 'week':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
    }

    const newDateRange = {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };

    // Update both period and date range together to prevent multiple re-renders
    setSelectedPeriod(period);
    setDateRange(newDateRange);

    console.log('📅 Period and date range updated:', { period, dateRange: newDateRange });
  };

  const generateReport = async (reportType: string) => {
    try {
      console.log('Generating report:', { reportType, dateRange });

      const response = await api.get('/firm-admin/reports/generate', {
        params: {
          reportType,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          format: 'json'
        }
      });

      if (response.data.success) {
        toast.success('Report generated successfully');
        fetchReports(); // Refresh reports list
      } else {
        toast.error(response.data.message || 'Failed to generate report');
      }
    } catch (error: any) {
      console.error('Error generating report:', error);
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to generate report';
      toast.error(errorMessage);
    }
  };

  const exportReport = async (report: Report) => {
    try {
      // Use the report's actual format
      const format = report.format || 'pdf';

      const response = await api.get(`/firm-admin/reports/export/${report.id}`, {
        params: { format },
        responseType: 'blob'
      });

      // Determine the correct MIME type based on format
      const mimeTypes = {
        pdf: 'application/pdf',
        csv: 'text/csv',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        json: 'application/json'
      };

      const blob = new Blob([response.data], {
        type: mimeTypes[format as keyof typeof mimeTypes] || 'application/octet-stream'
      });

      // Map format to correct file extension
      const extensionMap = {
        excel: 'xlsx',
        pdf: 'pdf',
        csv: 'csv',
        json: 'json'
      };
      const fileExtension = extensionMap[format as keyof typeof extensionMap] || format;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.name || `report-${report.id}`}.${fileExtension}`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success('Report exported successfully');
    } catch (error: any) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  const deleteReport = async (reportId: string) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;

    try {
      await api.delete(`/firm-admin/reports/${reportId}`);
      toast.success('Report deleted successfully');
      fetchReports(); // Refresh reports list
    } catch (error: any) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  // Report Viewer handlers
  const handleViewReport = (reportType: string) => {
    setSelectedReportType(reportType);
    setViewerMode('report');
  };

  const handleGenerateReport = async (reportType: string) => {
    // This function is kept for compatibility but should not be used in viewer tab
    // Report generation should be done in Report Management tab
    console.log('Report generation should be done in Report Management tab');
    toast.info('Please use the Report Management tab to generate new reports');
  };

  const handleBackToViewer = () => {
    setSelectedReportType(null);
    setViewerMode('library');
  };

  if (loading && activeTab === 'analytics') {
    console.log('🔄 Showing loading screen (analytics tab):', { loading, activeTab });
    return (
      <FirmAdminLayout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
            <span className="ml-2 text-lg">Loading analytics...</span>
          </div>
        </div>
      </FirmAdminLayout>
    );
  }

  const stats = analytics ? [
    {
      title: 'Total Revenue',
      value: `$${safeToLocaleString(analytics.overview.totalRevenue)}`,
      change: `${analytics.trends.revenueGrowth >= 0 ? '+' : ''}${safeToFixed(analytics.trends.revenueGrowth)}%`,
      icon: DollarSign,
      color: 'bg-green-500',
      trend: analytics.trends.revenueGrowth >= 0 ? 'up' : 'down'
    },
    {
      title: 'Active Clients',
      value: (analytics.overview.activeClients || 0).toString(),
      change: `${analytics.trends.clientGrowth >= 0 ? '+' : ''}${safeToFixed(analytics.trends.clientGrowth)}%`,
      icon: Users,
      color: 'bg-blue-500',
      trend: analytics.trends.clientGrowth >= 0 ? 'up' : 'down'
    },
    {
      title: 'Billable Hours',
      value: safeToFixed(analytics.overview.billableHours),
      change: `${safeToFixed(analytics.overview.utilizationRate)}% utilization`,
      icon: Clock,
      color: 'bg-purple-500',
      trend: 'neutral'
    },
    {
      title: 'Completed Tasks',
      value: (analytics.overview.completedTasks || 0).toString(),
      change: `${safeToFixed(analytics.trends.taskCompletionRate)}% completion rate`,
      icon: FileText,
      color: 'bg-orange-500',
      trend: 'neutral'
    }
  ] : [];

  // Show loading state
  if (loading) {
    console.log('🔄 Showing loading screen (general):', { loading, activeTab });
    return (
      <FirmAdminLayout>
        <div className="p-6">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
              <p className="text-gray-600">Loading Reports & Analytics...</p>
              <div className="mt-4 space-x-2">
                <button
                  onClick={() => {
                    console.log('🔧 Force setting loading to false');
                    setLoading(false);
                  }}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                >
                  🚀 FORCE SKIP LOADING
                </button>
                <button
                  onClick={() => {
                    console.log('🔧 Current state:', { loading, hasError });
                    console.log('🔧 Analytics data:', analytics);
                    console.log('🔧 Reports data:', reports);
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                >
                  🔍 DEBUG STATE
                </button>
                <button
                  onClick={() => {
                    console.log('🔄 Triggering manual data fetch...');
                    setLoading(true);
                    setTimeout(() => {
                      setLoading(false);
                    }, 1000);
                  }}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                >
                  🔄 RELOAD DATA
                </button>
              </div>
            </div>
          </div>
        </div>
      </FirmAdminLayout>
    );
  }

  // Render management tab content (will be handled in the main render below)

  // Render export tab content (will be handled in the main render below)

  // All tab content is now handled in the main render below

  console.log('✅ Rendering main analytics content!', {
    loading,
    hasError,
    activeTab,
    analytics: !!analytics,
    reportsCount: reports.length
  });

  return (
    <>
      <FirmAdminLayout>
        <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Reports & Analytics</h1>
              <p className="text-gray-600">Track performance, analyze trends, and generate insights</p>
            </div>
            <button
              onClick={fetchAnalytics}
              disabled={refreshing}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={`mr-2 h-5 w-5 ${
                      activeTab === tab.id ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                    }`} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'analytics' && (
          <>
            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex gap-2">
                <select
                  value={selectedPeriod}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="week">Last 7 days</option>
                  <option value="month">Last 30 days</option>
                  <option value="quarter">Last 3 months</option>
                  <option value="year">Last year</option>
                </select>

                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />

                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-2 sm:ml-auto">
                <div className="relative">
                  <select
                    onChange={(e) => e.target.value && generateReport(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors appearance-none pr-8"
                    defaultValue=""
                  >
                    <option value="" disabled>Generate Report</option>
                    <option value="financial">Financial Report</option>
                    <option value="task-performance">Task Performance Report</option>
                    <option value="client-activity">Client Activity Report</option>
                    <option value="time-tracking">Time Tracking Report</option>
                    <option value="attendance">Attendance Report</option>
                  </select>
                  <Download className="w-4 h-4 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400" />
                </div>

                <button
                  onClick={() => generateReport('comprehensive')}
                  className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Report
                </button>
              </div>
            </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className={`text-sm flex items-center mt-1 ${
                    stat.trend === 'up' ? 'text-green-600' :
                    stat.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {stat.trend === 'up' && <TrendingUp className="w-3 h-3 mr-1" />}
                    {stat.trend === 'down' && <TrendingUp className="w-3 h-3 mr-1 rotate-180" />}
                    {stat.trend === 'neutral' && <BarChart3 className="w-3 h-3 mr-1" />}
                    {stat.change}
                  </p>
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Revenue Chart */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Revenue Trend</h3>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <div className="h-64">
              {analytics?.charts.revenueByMonth.length ? (
                <div className="h-full flex items-end justify-between space-x-2">
                  {analytics.charts.revenueByMonth.map((item, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full bg-primary-500 rounded-t-sm min-h-[4px] transition-all duration-300 hover:bg-primary-600"
                        style={{
                          height: `${Math.max(4, analytics.charts.revenueByMonth.length > 0 ? (Number(item.revenue || 0) / Math.max(...analytics.charts.revenueByMonth.map(r => Number(r.revenue || 0)), 1)) * 200 : 4)}px`
                        }}
                        title={`${item.month}: $${safeToLocaleString(item.revenue)}`}
                      />
                      <span className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-left">
                        {item.month}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No revenue data available</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Task Status Distribution */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Task Status Distribution</h3>
              <FileText className="w-5 h-5 text-gray-400" />
            </div>
            <div className="h-64">
              {analytics?.charts.tasksByStatus.length ? (
                <div className="space-y-4">
                  {analytics.charts.tasksByStatus.map((item, index) => {
                    const total = analytics.charts.tasksByStatus.reduce((sum, task) => sum + (Number(task.count) || 0), 0);
                    const percentage = total > 0 ? ((Number(item.count) || 0) / total) * 100 : 0;
                    const colors = ['bg-green-500', 'bg-blue-500', 'bg-yellow-500', 'bg-red-500', 'bg-purple-500'];

                    return (
                      <div key={index} className="flex items-center">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 capitalize">
                              {item.status.toLowerCase().replace('_', ' ')}
                            </span>
                            <span className="text-sm text-gray-500">{item.count}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${colors[index % colors.length]}`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No task data available</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Hours Distribution */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Hours Distribution</h3>
              <Clock className="w-5 h-5 text-gray-400" />
            </div>
            <div className="h-64">
              {analytics?.charts.hoursDistribution.length ? (
                <div className="h-full flex items-center justify-center">
                  <div className="space-y-4 w-full">
                    {analytics.charts.hoursDistribution.map((item, index) => {
                      const total = analytics.charts.hoursDistribution.reduce((sum, h) => sum + (Number(h.hours) || 0), 0);
                      const percentage = total > 0 ? ((Number(item.hours) || 0) / total) * 100 : 0;

                      return (
                        <div key={index} className="flex items-center">
                          <div className="w-24 text-sm font-medium text-gray-700">{item.category}</div>
                          <div className="flex-1 mx-4">
                            <div className="w-full bg-gray-200 rounded-full h-4">
                              <div
                                className={`h-4 rounded-full ${index === 0 ? 'bg-green-500' : 'bg-gray-400'}`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                          <div className="w-16 text-sm text-gray-500 text-right">
                            {safeToFixed(item.hours)}h
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No hours data available</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Client Types */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Client Types</h3>
              <Users className="w-5 h-5 text-gray-400" />
            </div>
            <div className="h-64">
              {analytics?.charts.clientsByType.length ? (
                <div className="space-y-3">
                  {analytics.charts.clientsByType.map((item, index) => {
                    const total = analytics.charts.clientsByType.reduce((sum, client) => sum + (Number(client.count) || 0), 0);
                    const percentage = total > 0 ? ((Number(item.count) || 0) / total) * 100 : 0;
                    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-red-500'];

                    return (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full ${colors[index % colors.length]} mr-3`} />
                          <span className="text-sm font-medium text-gray-700 capitalize">
                            {item.type.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-sm text-gray-500 mr-2">{item.count}</span>
                          <span className="text-xs text-gray-400">({safeToFixed(percentage)}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No client data available</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Recent Reports</h3>
              <button
                onClick={fetchReports}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            {reports.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Report Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Generated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{report.name}</div>
                        <div className="text-sm text-gray-500">{report.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          report.type === 'financial' ? 'bg-blue-100 text-blue-800' :
                          report.type === 'productivity' ? 'bg-green-100 text-green-800' :
                          report.type === 'client-activity' ? 'bg-purple-100 text-purple-800' :
                          report.type === 'time-tracking' ? 'bg-orange-100 text-orange-800' :
                          report.type === 'attendance' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {report.type.charAt(0).toUpperCase() + report.type.slice(1).replace('-', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(report.generatedAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          report.status === 'ready' ? 'bg-green-100 text-green-800' :
                          report.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {report.status === 'ready' && (
                            <>
                              <button
                                onClick={() => exportReport(report)}
                                className="text-primary-600 hover:text-primary-900 p-1"
                                title={`Download ${(report.format || 'PDF').toUpperCase()}`}
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {/* TODO: Implement email functionality */}}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="Send via Email"
                              >
                                <Mail className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {report.status === 'processing' && (
                            <span className="text-gray-400 text-xs">Generating...</span>
                          )}
                          {report.status === 'failed' && (
                            <span className="text-red-400 text-xs">Failed</span>
                          )}
                          <button
                            onClick={() => deleteReport(report.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete Report"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Reports Generated</h3>
                <p className="text-gray-500 mb-4">Generate your first report to see it here.</p>
                <button
                  onClick={() => generateReport('financial')}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Generate Financial Report
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => generateReport('financial')}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <DollarSign className="w-5 h-5 mr-2 text-green-600" />
              <span className="text-sm font-medium">Financial Report</span>
            </button>
            <button
              onClick={() => generateReport('task-performance')}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
              <span className="text-sm font-medium">Task Performance Report</span>
            </button>
            <button
              onClick={() => generateReport('time-tracking')}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Clock className="w-5 h-5 mr-2 text-purple-600" />
              <span className="text-sm font-medium">Time Tracking</span>
            </button>
            <button
              onClick={() => generateReport('attendance')}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Users className="w-5 h-5 mr-2 text-orange-600" />
              <span className="text-sm font-medium">Attendance Report</span>
            </button>
          </div>
        </div>
          </>
        )}

        {/* Advanced Analytics Tab */}
        {activeTab === 'advanced' && (
          <div>
            <AdvancedAnalytics
              dateRange={dateRange}
              selectedPeriod={selectedPeriod}
              onDateRangeChange={handleDateRangeChange}
              onPeriodChange={handlePeriodChange}
            />
          </div>
        )}

        {/* Report Viewer Tab */}
        {activeTab === 'viewer' && (
          <div>
            {viewerMode === 'report' && selectedReportType ? (
              <ReportViewer
                reportType={selectedReportType}
                onBack={handleBackToViewer}
              />
            ) : (
              <ReportSelector
                onViewReport={handleViewReport}
              />
            )}
          </div>
        )}

        {/* Report Management Tab */}
        {activeTab === 'management' && (
          <div>
            <ReportsManagement />
          </div>
        )}

        {/* Data Export & Integration Tab */}
        {activeTab === 'export' && (
          <div>
            <DataExportIntegration />
          </div>
        )}

      </div>
    </FirmAdminLayout>
    </>
  );
};
